import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, CTA } from "../lib/site";
import { trackEvent } from "../lib/seo";

const Navbar = ({ minimal = false }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname, hash } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname, hash]);

    useEffect(() => {
        document.documentElement.style.overflow = open ? "hidden" : "";
        if (open) window.__lenis?.stop();
        else window.__lenis?.start();
        return () => {
            document.documentElement.style.overflow = "";
            window.__lenis?.start();
        };
    }, [open]);

    const isActive = (link) => {
        const base = link.href.split("#")[0];
        if (link.href.includes("#")) return pathname === base && hash === link.href.slice(base.length);
        if (link.children)
            return (
                pathname === base ||
                link.children.some((c) => pathname === c.href.split("#")[0])
            );
        return pathname === base;
    };

    return (
        <>
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-white/10 bg-[#0A0F1D]/85 backdrop-blur-md"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[80px] max-w-[1600px] items-center justify-between px-6 md:px-10 lg:px-16">
                <Link
                    to="/"
                    data-testid="navbar-logo-link"
                    className="shrink-0"
                    aria-label="BuildEase home"
                >
                    <Logo />
                </Link>

                {!minimal && (
                    <nav
                        aria-label="Primary"
                        className="hidden items-center gap-6 lg:flex"
                    >
                        {NAV_LINKS.map((link) =>
                            link.children ? (
                                <div key={link.label} className="group relative">
                                    <Link
                                        to={link.href}
                                        data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        aria-haspopup="true"
                                        className={`flex items-center gap-1 py-3 text-[15px] font-medium tracking-wide transition-colors duration-300 ${
                                            isActive(link)
                                                ? "font-semibold text-accent"
                                                : "text-mist hover:text-white"
                                        }`}
                                    >
                                        {link.label}
                                        <ChevronDown
                                            className="h-3.5 w-3.5 opacity-60"
                                            aria-hidden="true"
                                        />
                                    </Link>
                                    <div className="invisible absolute left-1/2 top-full z-10 w-64 -translate-x-1/2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                                        <div className="border border-white/12 bg-[#0C1428] py-2 shadow-[0_24px_60px_rgba(0,0,0,0.5)]">
                                            {link.children.map((child) => (
                                                <Link
                                                    key={child.label}
                                                    to={child.href}
                                                    data-testid={`nav-sublink-${child.label
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                                    className="block px-5 py-2.5 text-[14px] text-mist transition-colors duration-200 hover:bg-white/[0.04] hover:text-white"
                                                >
                                                    {child.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <Link
                                    key={link.label}
                                    to={link.href}
                                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`group relative py-3 text-[15px] font-medium tracking-wide transition-colors duration-300 ${
                                        isActive(link)
                                            ? "font-semibold text-accent"
                                            : "text-mist hover:text-white"
                                    }`}
                                >
                                    {link.label}
                                    <span
                                        className={`absolute bottom-1.5 left-0 h-px bg-accent transition-all duration-300 ${
                                            isActive(link)
                                                ? "w-full"
                                                : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </Link>
                            )
                        )}
                    </nav>
                )}

                <div className="flex items-center gap-3">
                    {!minimal && (
                        <Link
                            to={CTA.href}
                            data-testid="navbar-get-started"
                            onClick={() => trackEvent("get_started_click", "navbar")}
                            className="hidden items-center gap-1.5 bg-accent px-5 py-2.5 text-[15px] font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-[#ff7026] sm:inline-flex"
                        >
                            {CTA.label}
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                    )}
                    {!minimal && (
                        <button
                            type="button"
                            data-testid="mobile-menu-toggle"
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                            onClick={() => setOpen((v) => !v)}
                            className="inline-flex h-11 w-11 touch-manipulation select-none items-center justify-center border border-white/15 text-white transition-colors duration-300 hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:hidden"
                        >
                            {open ? (
                                <X className="h-5 w-5" />
                            ) : (
                                <Menu className="h-5 w-5" />
                            )}
                        </button>
                    )}
                </div>
            </div>
        </header>

        <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 top-[80px] z-[120] overflow-y-auto overscroll-contain bg-[#0A0F1D]/98 backdrop-blur-lg lg:hidden"
                    >
                        <nav
                            aria-label="Mobile"
                            className="flex flex-col px-6 py-10"
                        >
                            {NAV_LINKS.map((link, i) => (
                                <motion.div
                                    key={link.label}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: 0.04 + i * 0.04,
                                    }}
                                >
                                    <Link
                                        to={link.href}
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="flex items-baseline justify-between border-b border-white/10 pt-5 text-xl font-light tracking-wide text-white"
                                    >
                                        {link.label}
                                        <span className="font-mono text-[13px] text-cad">
                                            0{i + 1}
                                        </span>
                                    </Link>
                                    {link.children ? (
                                        <div className="border-b border-white/10 py-2">
                                            {link.children.map((child) => (
                                                <Link
                                                    key={child.label}
                                                    to={child.href}
                                                    data-testid={`mobile-nav-sublink-${child.label
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                                    className="block py-2 pl-4 text-[14px] text-mist"
                                                >
                                                    {child.label}
                                                </Link>
                                            ))}
                                        </div>
                                    ) : null}
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                <Link
                                    to={CTA.href}
                                    data-testid="mobile-get-started"
                                    onClick={() => trackEvent("get_started_click", "mobile_menu")}
                                    className="mt-10 flex items-center justify-center gap-2 bg-accent px-6 py-4 text-[16px] font-medium text-white"
                                >
                                    {CTA.label}
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </motion.div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
